import { Component, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { IconComponent } from '../../../icons/icon.component';

/**
 * Breadcrumb Item
 */
export interface BreadcrumbItem {
  label: string;
  route?: string;
  queryParams?: Record<string, string>;
  icon?: string;
  disabled?: boolean;
}

/**
 * Breadcrumb Component
 * 
 * Hierarchical navigation component that shows the user's location within the application.
 * Helps users understand where they are and navigate back to parent pages.
 * 
 * @example
 * ```typescript
 * // In your component
 * breadcrumbs: BreadcrumbItem[] = [
 *   { label: 'Home', route: '/', icon: '🏠' },
 *   { label: 'Portfólio', route: '/portfolio' },
 *   { label: 'Programas', route: '/portfolio/programas' },
 *   { label: 'Programa Alpha' } // Current page (no route)
 * ];
 * ```
 * 
 * ```html
 * <mg3p-breadcrumb
 *   [items]="breadcrumbs"
 *   [separator]="'/'"
 *   [showHome]="true"
 *   (itemClick)="onBreadcrumbClick($event)"
 * />
 * ```
 * 
 * Features:
 * - Hierarchical navigation
 * - Current page indication
 * - Custom separators
 * - Icon support
 * - Keyboard navigation
 * - Mobile responsive (collapses on small screens)
 * - WCAG 2.1 Level AA compliant
 */
@Component({
  selector: 'mg3p-breadcrumb',
  standalone: true,
  imports: [CommonModule, RouterModule, IconComponent],
  template: `
    <nav
      class="breadcrumb"
      [class.breadcrumb--collapsed]="collapsed()"
      [attr.aria-label]="ariaLabel()"
    >
      <ol class="breadcrumb__list" role="list">
        <!-- Home item (optional) -->
        @if (showHome() && items().length > 0) {
          <li class="breadcrumb__item">
            <a
              [routerLink]="homeRoute()"
              class="breadcrumb__link"
              [attr.aria-label]="homeLabel()"
            >
              @if (showHomeIcon()) {
                <span class="breadcrumb__icon"><mg3p-icon name="home" size="sm" /></span>
              }
              @if (!collapsed()) {
                <span class="breadcrumb__label">{{ homeLabel() }}</span>
              }
            </a>
            <span class="breadcrumb__separator" aria-hidden="true">{{ separator() }}</span>
          </li>
        }

        <!-- Breadcrumb items -->
        @for (item of items(); track $index; let isLast = $last; let isFirst = $first) {
          <li class="breadcrumb__item" [class.breadcrumb__item--current]="isLast">
            @if (isLast) {
              <!-- Current page (no link) -->
              <span
                class="breadcrumb__current"
                [attr.aria-current]="'page'"
              >
                @if (item.icon) {
                  <span class="breadcrumb__icon" aria-hidden="true">{{ item.icon }}</span>
                }
                <span class="breadcrumb__label">{{ item.label }}</span>
              </span>
            } @else {
              <!-- Clickable ancestor -->
              @if (collapsed() && !isFirst && $index < items().length - 2) {
                <!-- Show ellipsis for middle items on mobile -->
                @if ($index === 1) {
                  <button
                    type="button"
                    class="breadcrumb__ellipsis"
                    (click)="toggleCollapsed()"
                    [attr.aria-label]="'Mostrar ' + (items().length - 2) + ' itens ocultos'"
                  >
                    <span aria-hidden="true">...</span>
                  </button>
                  <span class="breadcrumb__separator" aria-hidden="true">{{ separator() }}</span>
                }
              } @else {
                <a
                  [routerLink]="item.route"
                  [queryParams]="item.queryParams"
                  class="breadcrumb__link"
                  [class.breadcrumb__link--disabled]="item.disabled"
                  (click)="onItemClicked(item, $event)"
                  [attr.aria-disabled]="item.disabled"
                >
                  @if (item.icon) {
                    <span class="breadcrumb__icon" aria-hidden="true">{{ item.icon }}</span>
                  }
                  <span class="breadcrumb__label">{{ item.label }}</span>
                </a>
                <span class="breadcrumb__separator" aria-hidden="true">{{ separator() }}</span>
              }
            }
          </li>
        }
      </ol>

      <!-- Mobile toggle (show all) -->
      @if (collapsed() && items().length > 3) {
        <button
          type="button"
          class="breadcrumb__toggle"
          (click)="toggleCollapsed()"
          [attr.aria-label]="'Mostrar caminho completo'"
        >
          <span aria-hidden="true">↓</span>
        </button>
      }
    </nav>
  `,
  styles: [`
    .breadcrumb {
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);
      padding: var(--spacing-sm) 0;
      font-size: var(--font-size-sm);
    }

    .breadcrumb__list {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: var(--spacing-xs);
      list-style: none;
      margin: 0;
      padding: 0;
    }

    .breadcrumb__item {
      display: flex;
      align-items: center;
      gap: var(--spacing-xs);
    }

    .breadcrumb__link,
    .breadcrumb__current,
    .breadcrumb__ellipsis {
      display: inline-flex;
      align-items: center;
      gap: var(--spacing-xs);
      padding: var(--spacing-xs) var(--spacing-sm);
      border-radius: var(--radius-sm);
      transition: all var(--transition-fast);
      text-decoration: none;
    }

    .breadcrumb__link {
      color: var(--color-text-secondary);
      background: transparent;
      border: none;
      cursor: pointer;
    }

    .breadcrumb__link:hover:not(.breadcrumb__link--disabled) {
      color: var(--color-primary);
      background: var(--color-primary-light);
    }

    .breadcrumb__link:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 2px;
    }

    .breadcrumb__link--disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .breadcrumb__current {
      color: var(--color-text-primary);
      font-weight: var(--font-weight-semibold);
      background: var(--color-surface-hover);
    }

    .breadcrumb__ellipsis {
      color: var(--color-text-secondary);
      background: transparent;
      border: 1px dashed var(--color-border);
      cursor: pointer;
      font-size: var(--font-size-lg);
      line-height: 1;
      padding: var(--spacing-xs);
    }

    .breadcrumb__ellipsis:hover {
      color: var(--color-primary);
      border-color: var(--color-primary);
      background: var(--color-primary-light);
    }

    .breadcrumb__ellipsis:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 2px;
    }

    .breadcrumb__icon {
      display: flex;
      align-items: center;
      color: var(--color-pmdf-primary-mid);
    }

    .breadcrumb__label {
      white-space: nowrap;
    }

    .breadcrumb__separator {
      color: var(--color-text-tertiary);
      user-select: none;
      font-size: var(--font-size-sm);
    }

    .breadcrumb__toggle {
      display: none;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      padding: 0;
      background: transparent;
      border: 1px solid var(--color-border);
      border-radius: var(--radius-sm);
      color: var(--color-text-secondary);
      cursor: pointer;
      transition: all var(--transition-fast);
    }

    .breadcrumb__toggle:hover {
      color: var(--color-primary);
      border-color: var(--color-primary);
      background: var(--color-primary-light);
    }

    .breadcrumb__toggle:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 2px;
    }

    /* Mobile responsive */
    @media (max-width: 768px) {
      .breadcrumb--collapsed .breadcrumb__item:not(:first-child):not(:last-child):not(:nth-last-child(2)) {
        display: none;
      }

      .breadcrumb__toggle {
        display: flex;
      }

      .breadcrumb__label {
        max-width: 150px;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    /* Reduced motion */
    @media (prefers-reduced-motion: reduce) {
      .breadcrumb__link,
      .breadcrumb__ellipsis,
      .breadcrumb__toggle {
        transition: none;
      }
    }
  `]
})
export class BreadcrumbComponent {
  // Inputs
  items = input.required<BreadcrumbItem[]>();
  separator = input<string>('/');
  showHome = input<boolean>(true);
  homeRoute = input<string>('/');
  homeLabel = input<string>('Home');
  homeIcon = input<string>('🏠');
  showHomeIcon = input<boolean>(true);
  ariaLabel = input<string>('Navegação estrutural');
  collapsed = input<boolean>(false);

  // Outputs
  itemClick = output<BreadcrumbItem>();
  collapsedChange = output<boolean>();

  onItemClicked(item: BreadcrumbItem, event: Event): void {
    if (item.disabled) {
      event.preventDefault();
      return;
    }
    
    this.itemClick.emit(item);
  }

  toggleCollapsed(): void {
    this.collapsedChange.emit(!this.collapsed());
  }
}

// Made with Bob
